/**
 * ProfilModal - Eigene Zugangsdaten ändern (E-Mail, Name, Passwort) + 2FA-Verwaltung
 */
import { useState } from 'react';
import { User, Mail, Lock, Eye, EyeOff, Save, X, AlertCircle, Check, ShieldCheck, ShieldOff, Copy, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

function TwoFactorSection() {
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState('idle'); // idle | setup | verify | backup | disabling
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const token = localStorage.getItem('olympus_token');
  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

  const startSetup = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/2fa/setup`, { method: 'POST', headers });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { detail: text }; }
      if (!res.ok) throw new Error(data.detail || 'Fehler beim Setup');
      setQrCode(data.qr_code);
      setSecret(data.secret);
      setStep('setup');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const confirmEnable = async () => {
    if (verifyCode.length !== 6) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/2fa/enable`, {
        method: 'POST', headers,
        body: JSON.stringify({ totp_code: verifyCode }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { detail: text }; }
      if (!res.ok) throw new Error(data.detail || 'Code ungültig');
      setBackupCodes(data.backup_codes || []);
      setStep('backup');
      try { await refreshUser(); } catch {}
      toast.success('2FA erfolgreich aktiviert');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const disable2FA = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/2fa/disable`, { method: 'POST', headers });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { detail: text }; }
      if (!res.ok) throw new Error(data.detail || 'Fehler');
      setStep('idle');
      try { await refreshUser(); } catch {}
      toast.success('2FA deaktiviert');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    toast.success('Backup-Codes kopiert');
  };

  const isEnabled = user?.totp_enabled;

  // Backup-Codes anzeigen
  if (step === 'backup') {
    return (
      <div className="space-y-3" data-testid="2fa-backup-section">
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm font-medium text-amber-800 mb-2">Backup-Codes sicher aufbewahren!</p>
          <p className="text-xs text-amber-700 mb-3">Diese Codes werden nur einmal angezeigt. Jeder Code ist nur einmal verwendbar.</p>
          <div className="grid grid-cols-2 gap-1.5">
            {backupCodes.map((code, i) => (
              <code key={i} className="text-sm bg-white px-2 py-1 rounded border border-amber-200 font-mono text-center">{code}</code>
            ))}
          </div>
          <Button size="sm" variant="outline" className="mt-3 w-full" onClick={copyBackupCodes} data-testid="2fa-copy-backup">
            <Copy className="w-3 h-3 mr-1.5" /> Alle kopieren
          </Button>
        </div>
        <Button size="sm" className="w-full" onClick={() => setStep('idle')} data-testid="2fa-backup-done">
          <Check className="w-3 h-3 mr-1.5" /> Fertig
        </Button>
      </div>
    );
  }

  // QR-Code Setup
  if (step === 'setup') {
    return (
      <div className="space-y-3" data-testid="2fa-setup-section">
        <p className="text-xs text-slate-600">Scannen Sie den QR-Code mit Ihrer Authenticator-App (Google Authenticator, Authy, etc.)</p>
        <div className="flex justify-center p-2 bg-white rounded-lg border border-slate-200">
          <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" data-testid="2fa-qr-code" />
        </div>
        <div className="text-center">
          <p className="text-[10px] text-slate-400 mb-1">Manueller Schlüssel:</p>
          <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono select-all" data-testid="2fa-manual-secret">{secret}</code>
        </div>
        {error && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{error}</p>}
        <div>
          <Label className="text-xs text-slate-600">Bestätigungscode eingeben</Label>
          <Input
            type="text" inputMode="numeric" maxLength={6} placeholder="000000"
            value={verifyCode}
            onChange={e => { setVerifyCode(e.target.value.replace(/\D/g, '')); setError(''); }}
            className="text-center text-lg tracking-[0.3em] font-mono mt-1"
            autoFocus
            data-testid="2fa-verify-input"
          />
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="flex-1" onClick={() => { setStep('idle'); setVerifyCode(''); setError(''); }} data-testid="2fa-setup-cancel">Abbrechen</Button>
          <Button size="sm" className="flex-1" onClick={confirmEnable} disabled={loading || verifyCode.length !== 6} data-testid="2fa-setup-confirm">
            {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <ShieldCheck className="w-3 h-3 mr-1.5" />}
            Aktivieren
          </Button>
        </div>
      </div>
    );
  }

  // Deaktivierung bestätigen
  if (step === 'disabling') {
    return (
      <div className="space-y-3" data-testid="2fa-disable-section">
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm font-medium text-red-800">2FA wirklich deaktivieren?</p>
          <p className="text-xs text-red-600 mt-1">Ihr Konto ist danach nur noch durch das Passwort geschützt.</p>
        </div>
        {error && <p className="text-xs text-red-500">{error}</p>}
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="flex-1" onClick={() => setStep('idle')} data-testid="2fa-disable-cancel">Abbrechen</Button>
          <Button size="sm" variant="destructive" className="flex-1" onClick={disable2FA} disabled={loading} data-testid="2fa-disable-confirm">
            {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <ShieldOff className="w-3 h-3 mr-1.5" />}
            Deaktivieren
          </Button>
        </div>
      </div>
    );
  }

  // Default: Status-Anzeige
  return (
    <div className="space-y-3" data-testid="2fa-status-section">
      {error && <p className="text-xs text-red-500">{error}</p>}
      <div className={`flex items-center justify-between p-3 rounded-lg border ${isEnabled ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
        <div className="flex items-center gap-2.5">
          <ShieldCheck className={`w-5 h-5 ${isEnabled ? 'text-emerald-600' : 'text-slate-400'}`} />
          <div>
            <p className={`text-sm font-medium ${isEnabled ? 'text-emerald-800' : 'text-slate-700'}`}>
              {isEnabled ? '2FA aktiv' : '2FA inaktiv'}
            </p>
            <p className="text-[11px] text-slate-500">
              {isEnabled ? 'Authenticator-App erforderlich beim Login' : 'Nur Passwort-Schutz aktiv'}
            </p>
          </div>
        </div>
        {isEnabled ? (
          <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setStep('disabling')} data-testid="2fa-disable-btn">
            <ShieldOff className="w-3 h-3 mr-1.5" /> Deaktivieren
          </Button>
        ) : (
          <Button size="sm" onClick={startSetup} disabled={loading} data-testid="2fa-enable-btn">
            {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <ShieldCheck className="w-3 h-3 mr-1.5" />}
            Aktivieren
          </Button>
        )}
      </div>
    </div>
  );
}

export default function ProfilModal({ open, onClose, asPage = false }) {
  const { user, refreshUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  const [form, setForm] = useState({
    vorname: user?.vorname || '',
    nachname: user?.nachname || '',
    email: user?.email || '',
    telefon: user?.telefon || '',
    aktuelles_passwort: '',
    neues_passwort: '',
    neues_passwort_confirm: '',
  });

  const [errors, setErrors] = useState({});

  if (!open) return null;

  // Als eigenständige Seite (ohne Modal-Overlay)
  if (asPage) {
    return (
      <div className="bg-white rounded-xl shadow-lg w-full max-w-lg mx-auto" data-testid="profil-page-content">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Mein Profil</h2>
              <p className="text-xs text-slate-500">Zugangsdaten und persoenliche Informationen</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {/* Persoenliche Daten */}
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
              <User className="w-4 h-4" /> Persoenliche Daten
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600">Vorname</Label>
                <Input value={form.vorname} onChange={e => updateField('vorname', e.target.value)} className={errors.vorname ? 'border-red-400' : ''} data-testid="profil-vorname" />
                {errors.vorname && <p className="text-xs text-red-500 mt-1">{errors.vorname}</p>}
              </div>
              <div>
                <Label className="text-xs text-slate-600">Nachname</Label>
                <Input value={form.nachname} onChange={e => updateField('nachname', e.target.value)} className={errors.nachname ? 'border-red-400' : ''} data-testid="profil-nachname" />
                {errors.nachname && <p className="text-xs text-red-500 mt-1">{errors.nachname}</p>}
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-xs text-slate-600">Telefon</Label>
              <Input value={form.telefon} onChange={e => updateField('telefon', e.target.value)} placeholder="Optional" data-testid="profil-telefon" />
            </div>
          </div>
          <Separator />
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
              <Mail className="w-4 h-4" /> E-Mail-Adresse (Login)
            </h3>
            <Input type="email" value={form.email} onChange={e => updateField('email', e.target.value)} className={errors.email ? 'border-red-400' : ''} data-testid="profil-email" />
            {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
          </div>
          <Separator />
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Zwei-Faktor-Authentifizierung (2FA)
            </h3>
            <TwoFactorSection />
          </div>
          <Separator />
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
              <Lock className="w-4 h-4" /> Passwort aendern
            </h3>
            <p className="text-xs text-slate-500 mb-3">Leer lassen, wenn Sie das Passwort nicht aendern moechten.</p>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-slate-600">Aktuelles Passwort</Label>
                <div className="relative">
                  <Input type={showPasswords ? 'text' : 'password'} value={form.aktuelles_passwort} onChange={e => updateField('aktuelles_passwort', e.target.value)} placeholder="Nur bei Passwort-Aenderung" className={errors.aktuelles_passwort ? 'border-red-400 pr-10' : 'pr-10'} data-testid="profil-aktuelles-passwort" />
                  <button type="button" onClick={() => setShowPasswords(!showPasswords)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.aktuelles_passwort && <p className="text-xs text-red-500 mt-1">{errors.aktuelles_passwort}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600">Neues Passwort</Label>
                  <Input type={showPasswords ? 'text' : 'password'} value={form.neues_passwort} onChange={e => updateField('neues_passwort', e.target.value)} placeholder="Mind. 6 Zeichen" className={errors.neues_passwort ? 'border-red-400' : ''} data-testid="profil-neues-passwort" />
                  {errors.neues_passwort && <p className="text-xs text-red-500 mt-1">{errors.neues_passwort}</p>}
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Passwort bestaetigen</Label>
                  <Input type={showPasswords ? 'text' : 'password'} value={form.neues_passwort_confirm} onChange={e => updateField('neues_passwort_confirm', e.target.value)} placeholder="Wiederholen" className={errors.neues_passwort_confirm ? 'border-red-400' : ''} data-testid="profil-passwort-confirm" />
                  {errors.neues_passwort_confirm && <p className="text-xs text-red-500 mt-1">{errors.neues_passwort_confirm}</p>}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <Button variant="outline" onClick={onClose} data-testid="profil-cancel-btn">Zurueck</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="profil-save-btn">
            {saving ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Speichere...</span> : <span className="flex items-center gap-2"><Save className="w-4 h-4" />Speichern</span>}
          </Button>
        </div>
      </div>
    );
  }

  // Als Modal (Original)

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const errs = {};
    if (!form.vorname.trim()) errs.vorname = 'Vorname erforderlich';
    if (!form.nachname.trim()) errs.nachname = 'Nachname erforderlich';
    if (!form.email.trim()) errs.email = 'E-Mail erforderlich';

    if (form.neues_passwort) {
      if (!form.aktuelles_passwort) errs.aktuelles_passwort = 'Aktuelles Passwort erforderlich';
      if (form.neues_passwort.length < 6) errs.neues_passwort = 'Mind. 6 Zeichen';
      if (form.neues_passwort !== form.neues_passwort_confirm) errs.neues_passwort_confirm = 'Passwörter stimmen nicht überein';
    }
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        vorname: form.vorname,
        nachname: form.nachname,
        email: form.email,
        telefon: form.telefon || null,
      };

      if (form.neues_passwort) {
        payload.aktuelles_passwort = form.aktuelles_passwort;
        payload.neues_passwort = form.neues_passwort;
      }

      const token = localStorage.getItem('olympus_token');
      const res = await fetch(`${BACKEND_URL}/api/auth/profil`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fehler beim Speichern');

      // Token aktualisieren falls E-Mail geändert
      if (data.new_token) {
        localStorage.setItem('olympus_token', data.new_token);
      }

      // Benutzer-Daten im Storage aktualisieren
      if (data.benutzer) {
        localStorage.setItem('olympus_user', JSON.stringify(data.benutzer));
      }

      await refreshUser();

      toast.success('Profil erfolgreich aktualisiert');
      setForm(prev => ({ ...prev, aktuelles_passwort: '', neues_passwort: '', neues_passwort_confirm: '' }));
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="profil-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Mein Profil</h2>
              <p className="text-xs text-slate-500">Zugangsdaten und persönliche Informationen</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-testid="profil-close-btn">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {/* Persönliche Daten */}
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
              <User className="w-4 h-4" /> Persönliche Daten
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600">Vorname</Label>
                <Input
                  value={form.vorname}
                  onChange={e => updateField('vorname', e.target.value)}
                  className={errors.vorname ? 'border-red-400' : ''}
                  data-testid="profil-vorname"
                />
                {errors.vorname && <p className="text-xs text-red-500 mt-1">{errors.vorname}</p>}
              </div>
              <div>
                <Label className="text-xs text-slate-600">Nachname</Label>
                <Input
                  value={form.nachname}
                  onChange={e => updateField('nachname', e.target.value)}
                  className={errors.nachname ? 'border-red-400' : ''}
                  data-testid="profil-nachname"
                />
                {errors.nachname && <p className="text-xs text-red-500 mt-1">{errors.nachname}</p>}
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-xs text-slate-600">Telefon</Label>
              <Input
                value={form.telefon}
                onChange={e => updateField('telefon', e.target.value)}
                placeholder="Optional"
                data-testid="profil-telefon"
              />
            </div>
          </div>

          <Separator />

          {/* E-Mail */}
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
              <Mail className="w-4 h-4" /> E-Mail-Adresse (Login)
            </h3>
            <Input
              type="email"
              value={form.email}
              onChange={e => updateField('email', e.target.value)}
              className={errors.email ? 'border-red-400' : ''}
              data-testid="profil-email"
            />
            {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
            {form.email !== user?.email && (
              <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Nach der Änderung müssen Sie sich mit der neuen E-Mail anmelden
              </p>
            )}
          </div>

          <Separator />

          {/* 2FA */}
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Zwei-Faktor-Authentifizierung (2FA)
            </h3>
            <TwoFactorSection />
          </div>

          <Separator />

          {/* Passwort */}
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
              <Lock className="w-4 h-4" /> Passwort ändern
            </h3>
            <p className="text-xs text-slate-500 mb-3">Leer lassen, wenn Sie das Passwort nicht ändern möchten.</p>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-slate-600">Aktuelles Passwort</Label>
                <div className="relative">
                  <Input
                    type={showPasswords ? 'text' : 'password'}
                    value={form.aktuelles_passwort}
                    onChange={e => updateField('aktuelles_passwort', e.target.value)}
                    placeholder="Nur bei Passwort-Änderung"
                    className={errors.aktuelles_passwort ? 'border-red-400 pr-10' : 'pr-10'}
                    data-testid="profil-aktuelles-passwort"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(!showPasswords)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.aktuelles_passwort && <p className="text-xs text-red-500 mt-1">{errors.aktuelles_passwort}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600">Neues Passwort</Label>
                  <Input
                    type={showPasswords ? 'text' : 'password'}
                    value={form.neues_passwort}
                    onChange={e => updateField('neues_passwort', e.target.value)}
                    placeholder="Mind. 6 Zeichen"
                    className={errors.neues_passwort ? 'border-red-400' : ''}
                    data-testid="profil-neues-passwort"
                  />
                  {errors.neues_passwort && <p className="text-xs text-red-500 mt-1">{errors.neues_passwort}</p>}
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Passwort bestätigen</Label>
                  <Input
                    type={showPasswords ? 'text' : 'password'}
                    value={form.neues_passwort_confirm}
                    onChange={e => updateField('neues_passwort_confirm', e.target.value)}
                    placeholder="Wiederholen"
                    className={errors.neues_passwort_confirm ? 'border-red-400' : ''}
                    data-testid="profil-passwort-confirm"
                  />
                  {errors.neues_passwort_confirm && <p className="text-xs text-red-500 mt-1">{errors.neues_passwort_confirm}</p>}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <Button variant="outline" onClick={onClose} data-testid="profil-cancel-btn">Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="profil-save-btn">
            {saving ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Speichere...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                Speichern
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
